<template>
    <div>
        <div class="flex flex-col min-h-0 h-full p-6 pt-5 | 2xl:p-8 2xl:pt-8">
            <page-header :title="$t('global.mainNav.appointments')">
                <nio-button
                    icon="regular/download-bottom"
                    tag="router-link"
                    :to="{ name: 'appointments.import.upload'}"
                >
                    {{ $t('global.general.import') }}
                </nio-button>

                <nio-button
                    color="blue"
                    icon="regular/add"
                    @click="openAppointmentModal"
                >
                    {{ $t('global.general.add') }}
                </nio-button>
            </page-header>

            <content-box :loading="$wait.is('fetching.appointments')">
                <template #header>
                    <content-box-tabs :active-tab.sync="filters.tab" :tabs="tabs" />
                    <div class="flex items-center">
                        <content-box-calendar :value="selectedDate" @input="date => setSelectedDate({ date })" />
                        <content-box-search :placeholder="$t('appointments.appointmentsList.search')" v-model="filters.search" />
                    </div>
                </template>
                <template #body>
                    <nio-table
                        :columns="columns"
                        :rows="filteredAppointments"
                        :selections.sync="selections"
                        ref="body"
                    >
                        <template #no-results>
                            <figure class="mb-6 text-3xl select-none">
                                ¯\_(ツ)_/¯
                            </figure>
                            <p class="mb-2 text-xl">
                                {{ $t('appointments.appointmentsList.noAppointments') }}
                            </p>
                            <p class="mb-4">
                                {{ $t('appointments.appointmentsList.wouldYouLikeToAddOne') }}
                            </p>
                            <nio-button color="blue" icon="regular/add" @click="openAppointmentModal">
                                {{ $t('global.general.add') }}
                            </nio-button>
                        </template>

                        <template v-slot:[`column-${column.name}`]="{ row: appointment }" v-for="column in columns">
                            <appointment-list-item
                                :appointment="appointment"
                                :column="column.name"
                                :earliest-exclude-after-time="earliestExcludeAfterTime"
                                :excluded-makes="excludedMakes"
                                :latest-exclude-before-time="latestExcludeBeforeTime"
                                :truncate="column.truncate"
                                :key="column.name"
                                @action-click="onActionClick($event, appointment)"
                                @open-modal="openAppointmentModal(appointment)"
                            />
                        </template>
                    </nio-table>
                </template>
                <template #footer>
                    <div class="flex-1 flex items-center justify-start p-4">
                        <nio-dropdown :disabled="!selectedAppointments.length" placement="top-start">
                            <template #trigger>
                                <nio-button color="blue">
                                    {{ $t('appointments.appointmentsList.bulkAction') }}
                                    <nio-icon class="text-xs ml-2" name="regular/arrow-up-1" />
                                </nio-button>
                            </template>

                            <nio-dropdown-item
                                icon="regular/messages-bubble-typing-1"
                                @click="onBulkActionClick('sendSmsConfirmation')"
                            >
                                {{ $t('appointments.appointmentsList.sendSmsConfirmation') }}
                            </nio-dropdown-item>
                            <nio-dropdown-item
                                icon="regular/send-email-envelope"
                                @click="onBulkActionClick('sendEmailConfirmation')"
                            >
                                {{ $t('appointments.appointmentsList.sendEmailConfirmation') }}
                            </nio-dropdown-item>
                            <nio-dropdown-item
                                divided-top
                                icon="regular/calendar-check"
                                @click="onBulkActionClick('markConfirmed')"
                            >
                                {{ $t('appointments.appointmentsList.markConfirmed') }}
                            </nio-dropdown-item>
                            <nio-dropdown-item
                                icon="regular/calendar"
                                @click="onBulkActionClick('markUnknown')"
                            >
                                {{ $t('appointments.appointmentsList.markNotConfirmed') }}
                            </nio-dropdown-item>
                            <nio-dropdown-item
                                icon="regular/calendar-disable"
                                @click="onBulkActionClick('markCanceled')"
                            >
                                {{ $t('appointments.appointmentsList.markCanceled') }}
                            </nio-dropdown-item>
                            <nio-dropdown-item
                                class="hover:text-red-500"
                                divided-top
                                icon="regular/bin-2-alternate"
                                @click="onBulkActionClick('delete')"
                            >
                                {{ $t('appointments.appointmentsList.delete') }}
                            </nio-dropdown-item>
                        </nio-dropdown>
                    </div>
                </template>
            </content-box>
        </div>

        <appointment-modal />
        <send-confirmation-modal @confirm="reset" />
        <confirm-modal
            :opened.sync="openDelete"
            :title="$t('appointments.appointmentsList.deleteModal.header')"
            @confirm="deleteSelectedAppointments"
        >
            <p slot="message">
                {{ $t('appointments.appointmentsList.deleteModal.message') }}
            </p>
        </confirm-modal>
        <confirm-modal
            :opened.sync="openSendUpdated"
            :title="$t('appointments.appointmentsList.sendAppointmentUpdatedSmsModal.header')"
            @confirm="markAndSendSms"
            @deny="markWithoutSms"
        >
            <p slot="message">
                {{ $t('appointments.appointmentsList.sendAppointmentUpdatedSmsModal.message') }}
            </p>
        </confirm-modal>

        <filters-in-url :filters="filters" />
    </div>
</template>

<script>
    import { mapState, mapActions, mapMutations } from 'vuex';
    import AppointmentListItem from '@/views/Appointments/AppointmentListItem.vue';
    import AppointmentModal from '@/components/Modals/AppointmentModal.vue';
    import ConfirmModal from '@/components/Modals/ConfirmModal.vue';
    import ContentBox from '@/components/ContentBox.vue';
    import ContentBoxCalendar from '@/components/ContentBoxCalendar.vue';
    import ContentBoxSearch from '@/components/ContentBoxSearch.vue';
    import ContentBoxTabs from '@/components/ContentBoxTabs.vue';
    import FiltersInUrl from '@/components/Utils/FiltersInUrl.js';
    import PageHeader from '@/components/PageHeader.vue';
    import SendConfirmationModal from '@/components/Modals/SendConfirmationModal.vue';

    export default {
        components: {
            AppointmentListItem,
            AppointmentModal,
            ConfirmModal,
            ContentBox,
            ContentBoxCalendar,
            ContentBoxSearch,
            ContentBoxTabs,
            FiltersInUrl,
            PageHeader,
            SendConfirmationModal,
        },

        data() {
            return {
                selectedStatus: '',
                selections: [],
                toDelete: {},
                openDelete: false,
                openSendUpdated: false,
                filters: {
                    date: null,
                    search: '',
                    tab: 'all',
                },
            };
        },

        computed: {
            ...mapState([
                'authUser',
            ]),

            ...mapState('appointment', [
                'appointments',
                'selectedDate',
                'selectedAppointments',
            ]),

            ...mapState('automation', [
                'automations',
            ]),

            serviceAppointments() {
                return this.appointments.filter(appointment => appointment.category == 'appointment_service');
            },

            confirmedServiceAppointments() {
                return this.serviceAppointments.filter(appointment => appointment.status == 'confirmed');
            },

            unknownServiceAppointments() {
                return this.serviceAppointments.filter(appointment => !appointment.status || appointment.status == 'unknown');
            },

            canceledServiceAppointments() {
                return this.serviceAppointments.filter(appointment => appointment.status == 'canceled');
            },

            filteredAppointments() {
                let filteredAppointments = [];

                switch (this.filters.tab) {
                    case 'confirmed':
                        filteredAppointments = this.confirmedServiceAppointments;
                        break;

                    case 'unknown':
                        filteredAppointments = this.unknownServiceAppointments;
                        break;

                    case 'canceled':
                        filteredAppointments = this.canceledServiceAppointments;
                        break;

                    default:
                        filteredAppointments = this.serviceAppointments;
                        break;
                }

                return filteredAppointments
                    .filter(appointment => {
                        if (empty(this.filters.search)) {
                            return true;
                        }

                        const search = this.filters.search.toLowerCase();

                        // Time
                        if (this.formatTime(appointment.datetime).toLowerCase().includes(search)) {
                            return true;
                        }

                        // Name
                        if (this.formatFullName(appointment.contact).toLowerCase().includes(search)) {
                            return true;
                        }

                        // Number
                        if (appointment.contact_phone && appointment.contact_phone.number.includes(search)) {
                            return true;
                        }

                        if (appointment.contact_phone && this.formatPhone(appointment.contact_phone.number).includes(search)) {
                            return true;
                        }

                        // Reply
                        if (!empty(appointment.reply)) {
                            if (appointment.reply.toLowerCase().includes(search)) {
                                return true;
                            }
                        }

                        return false;
                    })
                    .sort((a, b) => {
                        const dateA = moment(a.datetime);
                        const dateB = moment(b.datetime);
                        const nameA = this.formatFullName(a.contact);
                        const nameB = this.formatFullName(b.contact);

                        if (dateA.isSame(dateB)) {
                            if (nameA == nameB) {
                                return 0;
                            }

                            if (nameA < nameB) {
                                return -1;
                            }

                            if (nameA > nameB) {
                                return 1;
                            }
                        }

                        if (dateA.isBefore(dateB)) {
                            return -1;
                        }

                        if (dateA.isAfter(dateB)) {
                            return 1;
                        }

                        return 0;
                    });
            },

            tabs() {
                return [
                    {
                        value: 'all',
                        text: `${this.$t('appointments.appointmentsList.allTab')} (${this.serviceAppointments.length})`,
                        icon: 'regular/navigation-menu',
                    },
                    {
                        value: 'confirmed',
                        text: `${this.$t('appointments.appointmentsList.confirmedTab')} (${this.confirmedServiceAppointments.length})`,
                        icon: 'regular/calendar-check',
                    },
                    {
                        value: 'unknown',
                        text: `${this.$t('appointments.appointmentsList.notConfirmedTab')} (${this.unknownServiceAppointments.length})`,
                        icon: 'regular/calendar',
                    },
                    {
                        value: 'canceled',
                        text: `${this.$t('appointments.appointmentsList.canceledTab')} (${this.canceledServiceAppointments.length})`,
                        icon: 'regular/calendar-disable',
                    },
                ];
            },

            latestExcludeBeforeTime() {
                const format = 'HH:mm:ss';
                let exclusionTime = '00:00:00';

                this.automations.forEach(automation => {
                    const previousLatestTime = moment(exclusionTime, format);
                    const currentTime = moment(automation.exclude_appointments_before, format);

                    if (automation.state == 'running' && currentTime.isAfter(previousLatestTime)) {
                        exclusionTime = automation.exclude_appointments_before;
                    }
                });

                // Offset if user has different timezone
                if (this.authUser.account.timezone != this.authUser.timezone) {
                    exclusionTime = moment.tz(exclusionTime, format, this.authUser.account.timezone).tz(this.authUser.timezone).format(format);
                }

                return exclusionTime;
            },

            earliestExcludeAfterTime() {
                const format = 'HH:mm:ss';
                let exclusionTime = '23:59:59';

                this.automations.forEach(automation => {
                    const previousEarliestTime = moment(exclusionTime, format);
                    const currentTime = moment(automation.exclude_appointments_after, format);

                    if (automation.state == 'running' && currentTime.isBefore(previousEarliestTime)) {
                        exclusionTime = automation.exclude_appointments_after;
                    }
                });

                // Offset if user has different timezone
                if (this.authUser.account.timezone != this.authUser.timezone) {
                    exclusionTime = moment.tz(exclusionTime, format, this.authUser.account.timezone).tz(this.authUser.timezone).format(format);
                }

                return exclusionTime;
            },

            excludedMakes() {
                const makes = [];

                this.automations.forEach(automation => {
                    if (automation.exclude_appointments_with_make.length) {
                        automation.exclude_appointments_with_make.forEach(make => {
                            const upperCaseMake = make.toUpperCase();

                            if (makes.indexOf(upperCaseMake) === -1) {
                                makes.push(upperCaseMake);
                            }
                        });
                    }
                });

                return makes;
            },

            columns() {
                return [
                    {
                        name: 'name',
                        label: this.$t('appointments.appointmentsList.name'),
                        icon: 'regular/single-neutral',
                        className: 'flex-3',
                    },
                    {
                        name: 'time',
                        label: this.$t('appointments.appointmentsList.time'),
                        icon: 'regular/time-clock-circle',
                        className: 'flex-2',
                    },
                    {
                        name: 'phone',
                        label: this.$t('appointments.appointmentsList.phone'),
                        icon: 'regular/phone',
                        className: 'flex-2',
                    },
                    {
                        name: 'email',
                        label: this.$t('appointments.appointmentsList.email'),
                        icon: 'regular/envelope',
                        className: 'flex-2',
                    },
                    {
                        name: 'sentStatus',
                        label: this.$t('appointments.appointmentsList.sentStatus'),
                        icon: 'regular/send-email',
                        className: 'flex-2',
                        truncate: false,
                    },
                    {
                        name: 'status',
                        label: this.$t('appointments.appointmentsList.status'),
                        icon: 'regular/question-circle',
                        className: 'flex-2',
                        truncate: false,
                    },
                    {
                        name: 'actions',
                        label: this.$t('appointments.appointmentsList.actions'),
                        icon: 'regular/flash-1',
                        className: 'flex-1',
                        align: 'center',
                        truncate: false,
                    },
                ];
            },
        },

        watch: {
            selectedDate: {
                immediate: true,
                handler(newDate) {
                    if (!newDate) {
                        return;
                    }

                    this.reset();
                    this.scrollTop();

                    this.filters.date = moment().isSame(newDate, 'day') ? 'today' : newDate;
                },
            },

            'filters.date'(date) {
                this.setSelectedDate({ date, fetch: false });
            },

            selections(newValue) {
                const appointments = this.filteredAppointments.filter(a => newValue.includes(a.id));

                this.setSelectedAppointments(appointments);
            },

            'authUser.account.id'(newValue) {
                if (!newValue) {
                    return;
                }

                this.fetchAppointments();
            },
        },

        methods: {
            ...mapActions('appointment', [
                'deleteAppointments',
                'fetchAppointments',
                'setSelectedDate',
            ]),

            ...mapMutations('appointment', [
                'deleteAppointment',
                'setSelectedAppointments',
                'updateAppointment',
            ]),

            async onActionClick(command, appointment) {
                this.selections = [appointment.id];
                this.setSelectedAppointments([appointment]);

                switch (command) {
                    case 'sendSmsConfirmation':
                        this.$modal.open('sendConfirmation', {
                            confirmationType: 'sms',
                        });
                        break;
                    case 'sendEmailConfirmation':
                        this.$modal.open('sendConfirmation', {
                            confirmationType: 'email',
                        });
                        break;
                    case 'markConfirmed':
                        this.selectedStatus = 'mark-confirmed';
                        this.openSendUpdated = true;
                        break;
                    case 'markUnknown':
                        this.markAllAsNewStatus('mark-unknown');
                        break;
                    case 'markCanceled':
                        this.selectedStatus = 'mark-canceled';
                        this.openSendUpdated = true;
                        break;
                    case 'delete':
                        this.triggerDelete(appointment);
                        break;
                    default:
                        break;
                }
            },

            onBulkActionClick(command) {
                switch (command) {
                    case 'delete':
                        this.openDelete = true;
                        break;
                    case 'sendSmsConfirmation':
                        this.$modal.open('sendConfirmation', {
                            confirmationType: 'sms',
                        });
                        break;
                    case 'sendEmailConfirmation':
                        this.$modal.open('sendConfirmation', {
                            confirmationType: 'email',
                        });
                        break;
                    case 'markConfirmed':
                        this.selectedStatus = 'mark-confirmed';
                        this.openSendUpdated = true;
                        break;
                    case 'markUnknown':
                        this.markAllAsNewStatus('mark-unknown');
                        break;
                    case 'markCanceled':
                        this.selectedStatus = 'mark-canceled';
                        this.openSendUpdated = true;
                        break;
                    default:
                        break;
                }
            },

            reset() {
                return new Promise(resolve => {
                    this.selections = [];
                    this.selectedStatus = '';
                    this.toDelete = {};

                    this.$nextTick(() => {
                        resolve();
                    });
                });
            },

            scrollTop() {
                if (this.$refs.body) {
                    this.$refs.body.$el.scrollTop = 0;
                }
            },

            markWithoutSms() {
                this.markAllAsNewStatus(this.selectedStatus);
            },

            markAndSendSms() {
                this.markAllAsNewStatus(this.selectedStatus, true);
            },

            markAllAsNewStatus(status, sendSms = false) {
                for (const appointment of this.selectedAppointments) {
                    switch (status) {
                        case 'mark-confirmed':
                            this.setStatus(appointment, 'confirmed', sendSms);
                            break;

                        case 'mark-unknown':
                            this.setStatus(appointment, 'unknown', sendSms);
                            break;

                        case 'mark-canceled':
                            this.setStatus(appointment, 'canceled', sendSms);
                            break;
                        default:
                            break;
                    }
                }

                this.reset();
            },

            triggerDelete(appointment) {
                this.toDelete = appointment;
                this.openDelete = true;
            },

            deleteSelectedAppointments() {
                let appointments = this.selectedAppointments;

                if (!empty(this.toDelete)) {
                    appointments = [this.toDelete];
                }

                this.deleteAppointments(appointments);

                this.reset();
            },

            async setStatus(appointment, status, sendSms) {
                this.updateAppointment({
                    id: appointment.id,
                    status,
                });

                const phones = appointment.contact.phones || [];
                const contactHasValidPhone = phones.some(phone => this.phoneIsValid(phone));

                try {
                    await this.$axios.put(`appointments/${appointment.id}/status`, {
                        status,
                        sendSms: contactHasValidPhone ? sendSms : false,
                    });
                } catch (error) {
                    this.$notify.show({
                        type: 'error',
                        message: error.response.status === 422 ? error.response.data.message : this.$t('appointments.sendConfirmationModal.sendingFailed'),
                    });
                }
            },

            openAppointmentModal(appointment = null) {
                this.$modal.open('appointment', { appointment });
            },
        },

        created() {
            // Start loading to prevent flash...
            this.$wait.start('fetching.appointments');
        },

        mounted() {
            this.$nextTick(() => {
                this.fetchAppointments();
            });
        },

        beforeRouteLeave(to, from, next) {
            this.reset().then(() => {
                next();
            });
        },
    };
</script>
